import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import insertSetService from '../services/insertSetService';
import slugService from '../services/slugService';
import InsertSetCard from '../components/InsertSetCard';
import PageGrid from '../components/PageGrid';
import Sidebar from '../components/Sidebar';
import SidebarSection from '../components/Sidebar/SidebarSection';
import SidebarRow from '../components/Sidebar/SidebarRow';
import SidebarImage from '../components/Sidebar/SidebarImage';
import usePageTitle from '../utils/usePageTitle';
import { getProductBreadcrumbs } from '../utils/breadcrumbUtils';
import { filterInsertSets } from '../utils/filters';
import './ProductPage.css';

const ProductPage = () => {
  const { productSlug } = useParams();
  const [product, setProduct] = useState(null);
  const [productStats, setProductStats] = useState({
    totalInsertSets: 0,
    totalCards: 0
  });

  // Fetch product data on initial load
  useEffect(() => {
    const fetchProduct = async () => {
      if (productSlug) {
        try {
          const productData = await slugService.getProductBySlug(productSlug);
          setProduct(productData);
          
          setProductStats({
            totalInsertSets: productData.totalInsertSets || 0,
            totalCards: productData.totalCards || 0
          });
        } catch (error) {
          console.error('Error fetching product data:', error);
        }
      }
    };
    
    fetchProduct();
  }, [productSlug]);
  
  // Function to fetch insert sets for a specific page
  const fetchInsertSets = useCallback(async (page, limit) => {
    if (!product) {
      return {
        items: [],
        currentPage: 1,
        totalPages: 1,
        totalItems: 0
      };
    }
    
    try {
      const response = await insertSetService.getInsertSetsByProductByPage(product, page, limit);
      
      return {
        items: response.insertSets,
        currentPage: response.currentPage,
        totalPages: response.totalPages,
        totalItems: response.totalInsertSets
      };
    } catch (error) {
      console.error('Error fetching insert sets:', error);
      throw error;
    }
  }, [product]);
  
  
  // Function to render each insert set card
  const renderInsertSetCard = useCallback((insertSet, index) => {
    return (
      <InsertSetCard 
        insertSet={insertSet}
        index={index}
        linkUrl={slugService.getInsertSetUrl(insertSet, product)}
      />
    );
  }, [product]);
  
  // Prepare the title including product info
  const title = product ? `${product.name} (${product.year})` : '';
  
  // Set the page title
  usePageTitle(product ? `${product.name} (${product.year})` : 'Product');
  
  // Render the sidebar content with product details
  const renderSidebar = useCallback(() => {
    if (!product) return null;
    
    return (
      <Sidebar title="Product Information">
        <SidebarImage
          src={product.imageUrl}
          alt={product.name}
        />

        <SidebarSection title="Details">
          <SidebarRow
            label="Product"
            value={product.name}
          />
          <SidebarRow
            label="Year"
            value={product.year}
          />
          {product.publisher && (
            <SidebarRow
              label="Publisher"
              value={product.publisher}
            />
          )}
          {product.description && (
            <SidebarRow
              label="Description"
              value={product.description}
            />
          )}
        </SidebarSection>
        
        <SidebarSection title="Statistics">
          <SidebarRow
            label="Insert Sets"
            value={
              typeof productStats.totalInsertSets === 'number' 
                ? productStats.totalInsertSets.toLocaleString() 
                : '0'
            }
          />
          <SidebarRow
            label="Total Cards"
            value={
              typeof productStats.totalCards === 'number' 
                ? productStats.totalCards.toLocaleString() 
                : '0'
            }
          />
        </SidebarSection>
      </Sidebar>
    );
  }, [product, productStats]);
  
  if(!product) {
    return <></>;
  }
  // Generate breadcrumb items for this product page
  const breadcrumbItems = getProductBreadcrumbs(product);

  return (
    <PageGrid
      title={title}
      fetchItems={fetchInsertSets}
      filterFunction={filterInsertSets}
      renderItem={renderInsertSetCard}
      renderSidebar={renderSidebar}
      placeholderText="Filter insert sets..."
      emptyStateMessage="No insert sets found for this product"
      itemsPerPage={100}
      backButton={{
        url: "/trading-card-sets",
        label: "Back"
      }}
      breadcrumbItems={breadcrumbItems}
    />
  );
};

export default ProductPage;